import { useMemo } from 'react';
import { useUserContext } from '@blocs.auth';
import { FEATURE_FLAGS, useFeatureExperiment } from '@blocs.features-experiments';
import { PlanProvider } from '@blocs.paywall';
import { HeadshotIneligibilityReason, HeadshotPlanTermCode } from '@minecraft.graphql-operations';
import { useArtistContext } from '../core/ArtistContext';

export type HeadshotsModalName = 'trial' | 'booked' | 'annual' | 'monthly' | 'free';

const getHeadshotModal = (input: {
  isInTrial: boolean;
  hasUpcomingSession: boolean;
  reason: HeadshotIneligibilityReason | null | undefined;
  planTermCode: HeadshotPlanTermCode | null | undefined;
  isBelowPremium: boolean;
}): HeadshotsModalName | null => {
  if (input.isInTrial) return 'trial';
  if (input.hasUpcomingSession) return 'booked';
  if (input.reason === HeadshotIneligibilityReason.NoCreditsRemaining) {
    return input.planTermCode === HeadshotPlanTermCode.Annual ? 'annual' : 'monthly';
  }
  if (input.isBelowPremium) return 'free';
  return null;
};

export const useHeadshotEligibility = () => {
  const { headshotEligibility, isFreeHeadshotsSubscriber, isHeadshotEligibilityResolved, isSwitchingArtist } =
    useArtistContext();
  const { subscription } = useUserContext();
  const headshotsDirectBooking = useFeatureExperiment(FEATURE_FLAGS.WEB_HEADSHOTS_DIRECT_BOOKING);

  const reason = headshotEligibility?.ineligibilityReason;
  const hasUpcomingSession = headshotEligibility?.upcomingSession != null;
  const creditsRemaining = headshotEligibility?.creditsRemaining ?? 0;
  const hasUsedSession = creditsRemaining < (headshotEligibility?.creditsTotal ?? 0);
  // The backend counts Essential plans as entitled, so Premium is still checked from the plan level here.
  const isBelowPremium = Boolean(isFreeHeadshotsSubscriber);
  // Apple-billed trials are left out of the trial upsell: its Upgrade action opens the web paywall.
  const isAppleBilled = (subscription?.plan?.provider as PlanProvider | null | undefined) === PlanProvider.APPLE;
  const isInTrial = reason === HeadshotIneligibilityReason.InTrial && !isBelowPremium && !isAppleBilled;

  const showNavLink =
    Boolean(headshotsDirectBooking) &&
    !isSwitchingArtist &&
    headshotEligibility != null &&
    (reason !== HeadshotIneligibilityReason.LocationNotSupported || hasUpcomingSession);

  const showSessionCount = showNavLink && creditsRemaining >= 1;

  const canViewBookingPage =
    showNavLink && Boolean(headshotEligibility?.isEligible) && !isBelowPremium && !hasUpcomingSession;

  const showHeadshotBanner = canViewBookingPage && !hasUsedSession;

  const isResolved = !isSwitchingArtist && isHeadshotEligibilityResolved;

  const headshotModal = showNavLink
    ? getHeadshotModal({
        isInTrial,
        hasUpcomingSession,
        reason,
        planTermCode: headshotEligibility?.planTermCode,
        isBelowPremium,
      })
    : null;

  return useMemo(
    () => ({
      canViewBookingPage,
      creditsRemaining,
      headshotModal,
      isResolved,
      showHeadshotBanner,
      showNavLink,
      showSessionCount,
    }),
    [canViewBookingPage, creditsRemaining, headshotModal, isResolved, showHeadshotBanner, showNavLink, showSessionCount]
  );
};

export type UseHeadshotEligibilityValues = ReturnType<typeof useHeadshotEligibility>;
